<template>
	<div class="workplace-view">
		<v-snackbar
			v-model="snackbar"
			:timeout="5000"
			:top="true"
			color="accent"
			class="black--text"
		>
			{{ snackbarText }}
			<v-btn class="blexon-button" small icon @click="snackbar = false">
				<v-icon>close</v-icon>
			</v-btn>
		</v-snackbar>

		<h2 class="title mb-1">Arbeitsplätze</h2>
		<p class="caption grey--text mb-2">
			Arbeitsplätze von rechts in das Raster ziehen, um einen Arbeitsplan zu definieren. Ein
			Arbeitsplatz aus dem Raster heraus auf die rechte Seite gezogen wird gelöscht.
		</p>

		<div class="layout-row">
			<!-- Raster (Arbeitsplan) -->
			<div class="grid">
				<div class="grid-row" v-for="r in gridRows" :key="r.row">
					<div class="row-action">
						<v-icon
							v-if="canInsertRow(r.row)"
							small
							class="row-btn insert-row"
							title="Leere Zeile hier einfügen – nachfolgende Arbeitsplätze rücken nach unten"
							@click="insertRow(r.row)"
							>add_circle_outline</v-icon
						>
						<v-icon
							v-if="canDeleteRow(r.row)"
							small
							class="row-btn delete-row"
							title="Leere Zeile löschen – nachfolgende Arbeitsplätze rücken nach oben"
							@click="deleteRow(r.row)"
							>remove_circle_outline</v-icon
						>
					</div>
					<div class="grid-cells">
						<div
							v-for="cell in r.cells"
							:key="cell.col + '-' + cell.row"
							class="cell"
							:class="{
								filled: !!cell.wp,
								invalid: cell.wp && !isValid(cell.wp),
							}"
							@dragover.prevent
							@drop="onDropCell(cell.col, cell.row)"
						>
							<template v-if="cell.wp">
								<div
									class="card-header"
									draggable="true"
									@dragstart="onGridDragStart(cell.wp)"
									@dragend="onDragEnd"
									:title="getHint(cell.wp)"
								>
									<v-icon small class="drag-handle">drag_indicator</v-icon>
									<span class="card-title">{{
										typeName(cell.wp.manufacturingWorkStepType)
									}}</span>
								</div>
								<div
									class="cell-label"
									:class="{ hidden: focusedField !== cell.wp.id + '-step' }"
								>
									Arbeitsgangname
								</div>
								<v-text-field
									class="cell-input"
									placeholder="Arbeitsgangname"
									title="Name des Arbeitsgangs in Oseon"
									v-model="cell.wp.workStepName"
									dense
									hide-details
									single-line
									@focus="focusedField = cell.wp.id + '-step'"
									@blur="focusedField = null"
									@change="onNameChange(cell.wp)"
								></v-text-field>
								<div
									class="cell-label"
									:class="{ hidden: focusedField !== cell.wp.id + '-place' }"
								>
									Arbeitsplatz- oder Arbeitsplatzgruppe-Name
								</div>
								<v-text-field
									class="cell-input"
									placeholder="Arbeitsplatz- oder Arbeitsplatzgruppe-Name"
									title="Name des Arbeitsplatzes oder der Arbeitsplatzgruppe in Oseon"
									v-model="cell.wp.workplaceName"
									dense
									hide-details
									single-line
									@focus="focusedField = cell.wp.id + '-place'"
									@blur="focusedField = null"
									@change="onNameChange(cell.wp)"
								></v-text-field>
							</template>
						</div>
					</div>
				</div>
			</div>

			<!-- Palette (verfügbare Arbeitsplätze) -->
			<div
				class="palette"
				@dragover.prevent
				@drop="onDropPalette"
				:class="{ 'palette-drop': draggingFromGrid }"
			>
				<div class="palette-header">Verfügbare Arbeitsplätze</div>
				<div
					v-for="(item, index) in paletteWorkplaces"
					:key="item.manufacturingWorkStepType + '-' + index"
					class="palette-item"
					draggable="true"
					@dragstart="onPaletteDragStart(item)"
					@dragend="onDragEnd"
					title="In das Raster ziehen, um den Arbeitsplatz zu verwenden"
				>
					<div class="palette-item-title">
						<v-icon small class="drag-handle">drag_indicator</v-icon>
						{{ typeName(item.manufacturingWorkStepType) }}
					</div>
					<div v-if="item.workStepName || item.workplaceName" class="palette-item-names">
						{{ item.workStepName }}<span v-if="item.workplaceName"> / </span
						>{{ item.workplaceName }}
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import httpClient from "../store/httpClient";
import manufacturingWorkStepTypes from "../dtos/manufacturingWorkStepTypes";

const CUSTOM_STEP = 1000;

export default {
	data() {
		return {
			columns: 3,
			types: manufacturingWorkStepTypes,
			workplaces: [],
			dragData: null,
			focusedField: null,
			snackbar: false,
			snackbarText: "",
		};
	},

	mounted() {
		this.loadWorkplaces();
	},

	computed: {
		fertigerId() {
			return Number(this.$route.params.fertigerId);
		},
		draggingFromGrid() {
			return !!this.dragData && this.dragData.source === "grid";
		},
		placedWorkplaces() {
			return this.workplaces.filter((w) => this.isPlaced(w));
		},
		maxOccupiedRow() {
			return this.placedWorkplaces.reduce((max, w) => Math.max(max, w.yPosition), 0);
		},
		// Number of rows to display: all occupied rows plus one trailing empty row.
		rowCount() {
			return this.maxOccupiedRow + 1;
		},
		gridRows() {
			const rows = [];
			for (let row = 1; row <= this.rowCount; row++) {
				const cells = [];
				for (let col = 1; col <= this.columns; col++) {
					cells.push({ col, row, wp: this.cellWorkplace(col, row) });
				}
				rows.push({ row, cells });
			}
			return rows;
		},
		// One draggable entry per type that is still available on the right side.
		paletteWorkplaces() {
			const result = [];
			for (const type of this.types) {
				const t = type.manufacturingWorkStepType;
				if (t === CUSTOM_STEP) {
					// Custom steps can be added as often as wanted, so there is
					// always a fresh template to drag into the grid. Any unplaced
					// custom records (should be rare) are kept as well.
					this.workplaces
						.filter(
							(w) => w.manufacturingWorkStepType === CUSTOM_STEP && !this.isPlaced(w)
						)
						.forEach((w) => result.push(w));
					result.push(this.newTemplate(type));
					continue;
				}
				// If a workplace of this type is already in the grid, it is no
				// longer available on the right side (only one per type).
				if (this.placedWorkplaces.some((w) => w.manufacturingWorkStepType === t)) {
					continue;
				}
				// Reuse an existing unplaced record (keeps its names) or a fresh template.
				const existing = this.workplaces.find(
					(w) => w.manufacturingWorkStepType === t && !this.isPlaced(w)
				);
				result.push(existing || this.newTemplate(type));
			}
			return result;
		},
	},

	methods: {
		isPlaced(workplace) {
			return workplace.xPostion >= 1 && workplace.yPosition >= 1;
		},
		isValid(workplace) {
			return !!workplace.workStepName && !!workplace.workplaceName;
		},
		cellWorkplace(col, row) {
			return this.workplaces.find((w) => w.xPostion === col && w.yPosition === row);
		},
		typeName(type) {
			const match = this.types.find((t) => t.manufacturingWorkStepType === type);
			return match ? match.displayName : "";
		},
		getHint(workplace) {
			return this.isValid(workplace)
				? "Verfügbar"
				: "Arbeitsgang- und Arbeitsplatzname müssen definiert sein.";
		},
		newTemplate(type) {
			return {
				id: 0,
				fertigerId: this.fertigerId,
				manufacturingWorkStepType: type.manufacturingWorkStepType,
				workStepName: "",
				workplaceName: "",
				xPostion: 0,
				yPosition: 0,
			};
		},
		onPaletteDragStart(item) {
			this.dragData = { source: "palette", workplace: item };
		},
		onGridDragStart(workplace) {
			this.dragData = { source: "grid", workplace };
		},
		onDragEnd() {
			this.dragData = null;
		},
		async onDropCell(col, row) {
			if (!this.dragData) {
				return;
			}
			// Do not drop onto an occupied cell.
			if (this.cellWorkplace(col, row)) {
				this.dragData = null;
				return;
			}
			const workplace = this.dragData.workplace;
			this.dragData = null;
			workplace.xPostion = col;
			workplace.yPosition = row;
			await this.saveWorkplace(workplace);
			await this.loadWorkplaces();
		},
		async onDropPalette() {
			if (!this.dragData || this.dragData.source !== "grid") {
				this.dragData = null;
				return;
			}
			const workplace = this.dragData.workplace;
			this.dragData = null;
			if (workplace.id > 0) {
				await this.deleteWorkplace(workplace);
				await this.loadWorkplaces();
			}
		},
		isRowEmpty(row) {
			return !this.placedWorkplaces.some((w) => w.yPosition === row);
		},
		// The trailing empty row (rowCount) cannot be extended.
		canInsertRow(row) {
			return row <= this.maxOccupiedRow;
		},
		// Only empty rows above the trailing empty row can be deleted.
		canDeleteRow(row) {
			return row <= this.maxOccupiedRow && this.isRowEmpty(row);
		},
		async insertRow(row) {
			// Shift every placed workplace from this row downward by one row.
			const toShift = this.placedWorkplaces.filter((w) => w.yPosition >= row);
			for (const workplace of toShift) {
				workplace.yPosition += 1;
				await this.saveWorkplace(workplace);
			}
			await this.loadWorkplaces();
		},
		async deleteRow(row) {
			if (!this.canDeleteRow(row)) {
				return;
			}
			// Pull every workplace below the deleted row up by one row.
			const toShift = this.placedWorkplaces.filter((w) => w.yPosition > row);
			for (const workplace of toShift) {
				workplace.yPosition -= 1;
				await this.saveWorkplace(workplace);
			}
			await this.loadWorkplaces();
		},
		async onNameChange(workplace) {
			workplace.workStepName = (workplace.workStepName || "").trim();
			workplace.workplaceName = (workplace.workplaceName || "").trim();
			await this.saveWorkplace(workplace);
			if (!this.isValid(workplace)) {
				this.setSnackbarText(
					"Achtung: Arbeitsgang- und Arbeitsplatzname müssen definiert sein."
				);
			} else {
				this.setSnackbarText("Änderung erfolgreich gespeichert.");
			}
		},
		async loadWorkplaces() {
			const response = await httpClient().get(
				`api/Fertiger/GetWorkplaces?fertigerId=${this.fertigerId}`
			);
			this.workplaces = response.data;
		},
		async saveWorkplace(workplace) {
			await httpClient().post(
				`api/Fertiger/UpdateWorkplace?fertigerId=${this.fertigerId}`,
				workplace
			);
		},
		async deleteWorkplace(workplace) {
			await httpClient().delete(
				`api/Fertiger/DeleteWorkplace?fertigerId=${this.fertigerId}&id=${workplace.id}`
			);
		},
		setSnackbarText(text) {
			this.snackbarText = text;
			this.snackbar = true;
		},
	},
};
</script>

<style scoped>
.workplace-view {
	padding: 1em;
}
.layout-row {
	display: flex;
	align-items: flex-start;
	gap: 1em;
}
.grid {
	flex: 1 1 auto;
}
.grid-row {
	display: flex;
	align-items: stretch;
	gap: 6px;
	margin-bottom: 6px;
}
.row-action {
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	width: 18px;
}
.row-btn {
	cursor: pointer;
	color: #bdbdbd;
}
.insert-row:hover {
	color: #1976d2;
}
.delete-row:hover {
	color: #e53935;
}
.grid-cells {
	flex: 1 1 auto;
	display: grid;
	grid-template-columns: repeat(3, 1fr);
	gap: 6px;
}
.cell {
	min-height: 88px;
	border: 1px dashed #c0c0c0;
	border-radius: 4px;
	padding: 4px 6px;
	background: #fafafa;
}
.cell.filled {
	border: 2px solid green;
	background: #ffffff;
}
.cell.invalid {
	border: 2px solid #e53935;
}
.card-header {
	display: flex;
	align-items: center;
	cursor: move;
	user-select: none;
	font-size: 12px;
	font-weight: 600;
	margin-bottom: 2px;
}
.card-title {
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}
.drag-handle {
	cursor: move;
	margin-right: 2px;
}
.cell-label {
	font-size: 9px;
	line-height: 1;
	color: #9e9e9e;
	margin-top: 2px;
}
.cell-label.hidden {
	visibility: hidden;
}
.cell-input {
	margin-top: 0;
	padding-top: 0;
}
.cell-input >>> input {
	font-size: 12px;
	padding: 2px 0;
}
.palette {
	flex: 0 0 200px;
	border: 1px solid #d0d0d0;
	border-radius: 4px;
	padding: 6px;
	background: #fafafa;
}
.palette.palette-drop {
	border: 2px dashed #e53935;
	background: #fdecea;
}
.palette-header {
	font-size: 12px;
	font-weight: 600;
	margin-bottom: 6px;
}
.palette-item {
	font-size: 12px;
	cursor: move;
	user-select: none;
	border: 1px solid orange;
	border-radius: 4px;
	background: #fff;
	padding: 4px 6px;
	margin-bottom: 4px;
}
.palette-item-title {
	display: flex;
	align-items: center;
	font-weight: 600;
}
.palette-item-names {
	margin-left: 20px;
	font-size: 11px;
	color: #2e7d32;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}
</style>
